import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';
import { useAuth } from '../../contexts/AuthContext';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  return (
    <>
      <Helmet>
        <title>Privacy Policy - MyBodyToday</title>
        <meta name="description" content="MyBodyToday Privacy Policy - How we collect, use, and protect your health information" />
      </Helmet>
      
      <div className="min-h-screen bg-background px-4 py-8">
        <div className="w-full max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <Link to="/sign-in" className="inline-flex items-center text-primary hover:text-primary/80">
                <Icon name="ArrowLeft" size={20} className="mr-2" />
                Back to Sign In
              </Link>
              <CloseButton navigateTo="/sign-in" ariaLabel="Close privacy policy" onClose={() => {}} />
              {isAuthenticated && <LogoutButton />}
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-2">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground">
              Last Updated: February 23, 2026
            </p>
          </div>

          {/* Content */}
          <div className="bg-card rounded-lg border border-border p-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                1. Introduction
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                MyBodyToday ("we," "our," or "us") is committed to protecting your privacy and the security of your personal health information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our symptom tracking application and services.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                2. Information We Collect
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    2.1 Personal Information
                  </h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Name, email address, and contact information</li>
                    <li>Account credentials (encrypted passwords)</li>
                    <li>Profile information (age, gender, user type)</li>
                    <li>Payment information (processed securely through Stripe)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    2.2 Health Information
                  </h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Symptom entries (category, severity, timing, location)</li>
                    <li>Medication tracking data</li>
                    <li>Medical conditions and allergies</li>
                    <li>Healthcare provider information</li>
                    <li>Photos and voice recordings (if you choose to provide them)</li>
                    <li>Daily check-in responses and health assessments</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    2.3 Usage Information
                  </h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Device information (browser type, operating system)</li>
                    <li>IP address and location data</li>
                    <li>App usage patterns and feature interactions</li>
                    <li>Session data and login history</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use your information to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Provide and maintain symptom tracking services</li>
                <li>Generate health reports and analytics</li>
                <li>Send reminders and notifications (with your consent)</li>
                <li>Enable caregiver access (when authorized by you)</li>
                <li>Process payments and manage subscriptions</li>
                <li>Improve our services and develop new features</li>
                <li>Comply with legal obligations</li>
                <li>Detect and prevent fraud or security issues</li>
              </ul>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                4. Data Security
              </h2>
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Icon name="Shield" size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-teal-900 mb-1">
                      Industry-Standard Encryption
                    </h3>
                    <p className="text-sm text-teal-800">
                      We use AES-256 encryption for sensitive health data and TLS/SSL for data transmission.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We implement comprehensive security measures including:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>End-to-end encryption for medical information</li>
                <li>Secure authentication with optional two-factor authentication</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Restricted access controls and audit logging</li>
                <li>Secure cloud infrastructure (Supabase)</li>
                <li>Automatic session management and timeout</li>
              </ul>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                5. Data Sharing and Disclosure
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We do NOT sell your personal or health information. We may share your information only in these circumstances:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li><strong>With Your Consent:</strong> When you authorize caregivers or healthcare providers to access your data</li>
                <li><strong>Service Providers:</strong> With trusted third parties who help us operate our services (e.g., Stripe for payments, Resend for emails)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
                <li><strong>Emergency Situations:</strong> To protect your vital interests or those of others</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice to you)</li>
              </ul>
            </section>

            {/* Third-Party Data Processors */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                5a. Third-Party Data Processors
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use the following trusted third-party services to operate MyBodyToday. Each acts as a data processor on our behalf and is bound by appropriate data processing agreements:
              </p>
              <div className="space-y-4">
                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Database" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">Supabase (Database &amp; Authentication)</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Supabase provides our secure cloud database and authentication infrastructure. Your account credentials, health data, and symptom entries are stored on Supabase servers. Supabase is GDPR-compliant and processes data in accordance with their{' '}
                        <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80">Privacy Policy</a>.
                        Data is encrypted at rest and in transit.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="CreditCard" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">Stripe (Payment Processing)</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Stripe processes all subscription payments for MyBodyToday Premium (£4.99/month). When you subscribe, your payment card details are collected and stored directly by Stripe — we never store your full card number. Stripe is PCI-DSS Level 1 certified and processes data in accordance with their{' '}
                        <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80">Privacy Policy</a>.
                        Billing records (amount, date, status) are retained for legal and accounting purposes.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="BarChart2" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">Google Analytics 4 (Analytics — with your consent)</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        We use Google Analytics 4 to understand how users interact with our app. GA4 is only activated after you grant Analytics consent in our cookie banner. No personally identifiable health information is sent to Google. You can withdraw consent at any time via{' '}
                        <Link to="/settings-and-privacy" className="text-primary underline underline-offset-2 hover:text-primary/80">Settings &amp; Privacy</Link>.
                        Google processes analytics data in accordance with their{' '}
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80">Privacy Policy</a>.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                6. Your Privacy Rights
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Export:</strong> Download your data in a portable format</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Revoke Consent:</strong> Withdraw consent for data processing at any time</li>
                <li><strong>Data Portability:</strong> Transfer your data to another service</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                To exercise these rights, visit your Settings & Privacy page or contact us at privacy@mybodytoday.com.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                7. Data Retention
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain your personal information for as long as your account is active or as needed to provide services. If you delete your account, we will permanently delete your data within 30 days, except where we are required to retain it for legal, regulatory, or security purposes. Anonymized data may be retained for analytics and service improvement.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                8. Children's Privacy
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
              </p>
            </section>

            {/* International Users */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                9. International Data Transfers
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable data protection laws.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                10. Changes to This Privacy Policy
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of the service after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                11. Contact Us
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-muted rounded-lg p-4 space-y-2">
                <p className="text-foreground">
                  <strong>Email:</strong> privacy@mybodytoday.com
                </p>
                <p className="text-foreground">
                  <strong>Support:</strong> support@mybodytoday.com
                </p>
                <p className="text-foreground">
                  <strong>Website:</strong> mybodytoday1.com
                </p>
              </div>
            </section>

            {/* Medical Disclaimer */}
            <section className="border-t border-border pt-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-amber-900 mb-1">
                      Medical Disclaimer
                    </h3>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      MyBodyToday is a symptom tracking tool and does not provide medical advice, diagnosis, or treatment. Always consult qualified healthcare professionals for medical guidance. In case of emergency, call your local emergency services immediately.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Footer Links */}
          <div className="mt-8 text-center space-x-4">
            <Link to="/terms-and-conditions" className="text-primary hover:underline">
              Terms and Conditions
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/sign-in" className="text-primary hover:underline">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;